/**
 * FIND-02 — persistent adaptive search history (spec §4.2). The single cheapest "feels psychic" win:
 * remember which result the user picked for which query, and float it to the top next time they type
 * that prefix. Stored in localStorage (like the note index) — small, per-device, and rebuildable.
 *
 * Pure decay/boost math lives in @lapo/core (frecency.ts, unit-tested); this is just the persistence
 * shell + pruning, so it stays browser-only and untested here.
 */
import { reinforce, pruneAdaptive, type AdaptiveEntry } from '@lapo/core';

const STORAGE_KEY = 'lapo-adaptive-history-v1';
/** Cap the store so a heavy user's history can't grow without bound. */
const MAX_ENTRIES = 500;

function read(): AdaptiveEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AdaptiveEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(entries: AdaptiveEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    /* quota / private mode — history is best-effort */
  }
}

/** Load the current (pruned) entries for feeding into `searchNotes`. */
export function loadAdaptiveHistory(now = Date.now()): AdaptiveEntry[] {
  return pruneAdaptive(read(), now);
}

/** Record that the user picked `pickedId` after typing `query`; persists the reinforced store. */
export function recordPick(query: string, pickedId: string, now = Date.now()): AdaptiveEntry[] {
  const next = pruneAdaptive(reinforce(read(), query, pickedId, now), now);
  write(next);
  return next;
}
